import { useState } from "react";
import { Button } from "@aidron/aidron-ds";
import jsPDF from "jspdf";
import { restoreNode, captureNode, prepareNode } from "../utils";
import { ErrorModal } from "../../../../components/SharedComponents/sharedComponents.index";
import { i18n } from "../../../../translate/i18n";

const { t } = i18n;

const handleInputsSave = async ({ onInputsSave, setShowInputsSaveError, setInputsSaveError}) => {
    try {
        const resultFromUpdate = await onInputsSave();

        if (resultFromUpdate?.inputs?.shouldVerify && !resultFromUpdate.inputs.success) {
            setShowInputsSaveError(true);
        }

        if (resultFromUpdate?.removal?.shouldVerify && !resultFromUpdate.removal.success) {
            setShowInputsSaveError(true);
        }
    } catch (err) {
        setInputsSaveError(err);
        setShowInputsSaveError(true);
    }
};

const handleSaveAsPDF = async ({ orderId, storeName, orderDate, currentSupplierName }) => {
    const modalNode = document.getElementById(`modal-${orderId}`);
    if (!modalNode) return;

    const columnsToHide = modalNode.querySelectorAll('.hide-on-export');
    const originalDisplayValues = [];

    const tableContainer = modalNode.querySelector('div[style*="overflowX"]');
    const originalTableOverflow = tableContainer ? tableContainer.style.overflowX : null;

    const originalStyles = prepareNode(modalNode, { width: modalNode.style.width });

    try {
    columnsToHide.forEach((element, index) => {
        originalDisplayValues[index] = element.style.display;
        element.style.display = 'none';
    });

    modalNode.style.width = 'auto';
    if (tableContainer) tableContainer.style.overflowX = 'visible';

    await new Promise(resolve => setTimeout(resolve, 100));

    const canvas = await captureNode(modalNode);

    canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onload = function(e) {
        const dataUrl = e.target.result;
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(dataUrl);
        const imgWidth = pageWidth;
        const imgHeight = (imgProps.height * pageWidth) / imgProps.width;

        if (imgHeight > pageHeight) {
            const scaleFactor = pageHeight / imgHeight;
            const adjustedWidth = imgWidth * scaleFactor;
            const xOffset = (pageWidth - adjustedWidth) / 2;
            pdf.addImage(dataUrl, "PNG", xOffset, 0, adjustedWidth, pageHeight);
        } else {
            const margin = 10;
            const adjustedWidth = pageWidth - (margin * 2);
            const adjustedHeight = (imgProps.height * adjustedWidth) / imgProps.width;
            pdf.addImage(dataUrl, "PNG", margin, margin, adjustedWidth, adjustedHeight);
        }

        pdf.save(`Loja-${storeName}-Data-${orderDate}-Fornecedor-${currentSupplierName}-Pedido-${orderId}.pdf`);
        };
        reader.readAsDataURL(blob);
    });

    } catch (err) {
    console.error("Erro ao salvar PDF:", err);
    alert("Erro ao exportar PDF. Tente novamente.");
    } finally {
    columnsToHide.forEach((element, index) => {
        element.style.display = originalDisplayValues[index];
    });
    restoreNode(modalNode, originalStyles);
    if (tableContainer && originalTableOverflow !== null) {
        tableContainer.style.overflowX = originalTableOverflow;
    }
    }
};

const handlePrint = async ({ orderId, storeName, currentSupplierName, orderDate }) => {
    const modalNode = document.getElementById(`modal-${orderId}`);
    if (!modalNode) return;

    const columnsToHide = modalNode.querySelectorAll('.hide-on-export');
    const originalDisplayValues = [];

    const tableContainer = modalNode.querySelector('div[style*="overflowX"]');
    const originalTableOverflow = tableContainer ? tableContainer.style.overflowX : null;

    const originalStyles = prepareNode(modalNode, { width: modalNode.style.width });

    try {
    columnsToHide.forEach((element, index) => {
        originalDisplayValues[index] = element.style.display;
        element.style.display = 'none';
    });

    modalNode.style.width = 'auto';
    if (tableContainer) tableContainer.style.overflowX = 'visible';

    await new Promise(resolve => setTimeout(resolve, 100));

    const canvas = await captureNode(modalNode);

    canvas.toBlob((blob) => {
        const fileName = `Loja-${storeName}-Fornecedor-${currentSupplierName}-Data-${orderDate}-Pedido-${orderId}.png`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    });

    } catch (err) {
    console.error("Erro ao exportar imagem:", err);
    alert("Erro ao exportar imagem. Tente novamente.");
    } finally {
    columnsToHide.forEach((element, index) => {
        element.style.display = originalDisplayValues[index];
    });
    restoreNode(modalNode, originalStyles);
    if (tableContainer && originalTableOverflow !== null) {
        tableContainer.style.overflowX = originalTableOverflow;
    }
    }
};

export const OrderDetailsModalBtn = ({ actions, ctx, uiState }) => {

    const [showInputsSaveError, setShowInputsSaveError] = useState(false);
    const [inputsSaveError, setInputsSaveError] = useState(null);

    const { 
        setShowCancelModal, 
        setShowInputs, 
        onInputsSave
    } = actions || {};

    const { 
        suppliersAndProductRelations,
        orderId, 
        orderDate, 
        storeName, 
        currentSupplierName
    } = ctx || {};

    const {
        pdfIcon,
        cameraIcon,
        showInputs,
        customDisabledCondition
    } = uiState || {};

    const PdfIconComponent = pdfIcon;
    const CameraIconComponent = cameraIcon;

    const editButtonDisabled = customDisabledCondition !== false 
        ? !customDisabledCondition 
        : !suppliersAndProductRelations?.length;    

    if (showInputs) {
      return (
        <>
          <Button type={'outlined'} onClick={() => setShowCancelModal(true)} label={t("cancelEdit")} />
          <Button onClick={() => handleInputsSave({ onInputsSave, setShowInputsSaveError, setInputsSaveError })} label={t("save")} />
        </>
      );
    } 

    return (
      <>
        <Button size="sm" type="ghost" onClick={() => handleSaveAsPDF({ orderId, orderDate, storeName, currentSupplierName  })}>
          <PdfIconComponent size={28} />
        </Button>

        <Button size="sm" type="ghost" onClick={() => handlePrint({ orderId, storeName, currentSupplierName, orderDate })}>
          <CameraIconComponent size={33} color="#f97316" />
        </Button>             

        <Button onClick={() => setShowInputs(true)} label={t("edit")} disabled={editButtonDisabled} />

        {showInputsSaveError && (
          <ErrorModal 
            errorStatus={inputsSaveError?.response?.status || "Erro inesperado"}
            onClose={() => setShowInputsSaveError(false)}
            isOpen={showInputsSaveError}             
          />
        )}
      </>
    );    
}